import axios, { AxiosInstance } from 'axios';
import { jwtDecode } from 'jwt-decode';

export function isJwtLoggedIn() {
    const jwt = localStorage.getItem('jwt');
    if (jwt) {
        try {
            const decodedToken: { exp: number } = jwtDecode(jwt);
            const currentTime = Date.now() / 1000;
            return currentTime < decodedToken.exp;
        } catch (error) {
            console.error('Error decoding JWT:', error);
            return false;
        }
    }
    return false;
}

interface ReturnLocation {
    pathname: string;
    search?: string;
    hash?: string;
}

/** The login page, told where to return afterwards and whether to say the session ended. */
export function loginLocationFor(location: ReturnLocation, sessionEnded: boolean) {
    return {
        pathname: '/login',
        state: {
            from: { pathname: location.pathname, search: location.search ?? '', hash: location.hash ?? '' },
            sessionEnded,
        },
    };
}

const SESSION_ENDED_EVENT = 'verbaschola:session-ended';

export function onSessionEnded(listener: () => void): () => void {
    window.addEventListener(SESSION_ENDED_EVENT, listener);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, listener);
}

/**
 * Announce 401s that mean the session is gone. The API also answers 401 for a course the
 * signed-in user may not open. `login_required` sends a plain-string "Token is invalid" for a
 * rejected token; the object-shaped message from `teacher_required` and `admin_required` also
 * means wrong role, so it cannot establish that the session ended. `GET /user` checks only the
 * session and is safe to use as a separate signal.
 */
export function reportEndedSessions(instance: AxiosInstance) {
    instance.interceptors.response.use(undefined, error => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
            const sessionCheck = error.config?.method?.toLowerCase() === 'get' && error.config.url === '/user';
            const invalidToken = error.response.data === 'Token is invalid';
            // An older request may finish after the visitor has logged in again. Its rejection
            // must not clear the newer session.
            if ((sessionCheck || invalidToken) && error.config?.headers?.['x-access-token'] === localStorage.getItem('jwt')) {
                localStorage.removeItem('jwt');
            }
            if (!isJwtLoggedIn()) window.dispatchEvent(new Event(SESSION_ENDED_EVENT));
        }
        return Promise.reject(error);
    });
}
